import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

export default function Terms() {
  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      <div className="pt-20">
        {/* Header */}
        <div className="bg-white border-b border-gray-200">
          <div className="max-w-4xl mx-auto px-4 py-12">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">Terms &amp; Conditions</h1>
            <p className="text-lg text-gray-600">
              Last Updated: January 15, 2025
            </p>
          </div>
        </div>

        {/* Content */}
        <div className="max-w-4xl mx-auto px-4 py-12">
          <div className="bg-white rounded-lg p-8 space-y-8">
            {/* Introduction */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Introduction</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Welcome to Lone Pine Public Square ("Platform"), a community governance platform operated for the benefit of Lone Pine Homeowners Association ("HOA") members. By accessing or using this Platform, you agree to be bound by these Terms and Conditions.
              </p>
              <p className="text-gray-700 leading-relaxed">
                This Platform is designed to facilitate transparent communication, document sharing, and community governance among verified HOA members. All users must comply with these terms to maintain access.
              </p>
            </section>

            {/* Eligibility */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Eligibility and Membership</h2>
              <div className="space-y-3 text-gray-700 leading-relaxed">
                <p><strong>2.1 Membership Requirements:</strong> Only verified members of Lone Pine HOA may create accounts and participate on this Platform. Membership requires:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Ownership or authorized occupancy of one of the 33 properties within Lone Pine HOA</li>
                  <li>Valid email address for verification purposes</li>
                  <li>Accurate property address information</li>
                  <li>Agreement to these Terms and Conditions</li>
                </ul>
                <p><strong>2.2 Account Types:</strong> The Platform recognizes the following account statuses:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>Pending:</strong> New accounts awaiting verification</li>
                  <li><strong>Verified Member:</strong> Full access to all platform features</li>
                  <li><strong>Linked Account:</strong> Additional accounts linked to the same property</li>
                  <li><strong>Moderator:</strong> Members with moderation responsibilities</li>
                  <li><strong>Suspended:</strong> Temporary restriction (7-30 days) for policy violations</li>
                  <li><strong>Banned:</strong> Permanent removal for serious or repeated violations</li>
                </ul>
              </div>
            </section>

            {/* Content Standards */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Content Standards and Acceptable Use</h2>
              <div className="space-y-4">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <h3 className="text-lg font-semibold text-green-800 mb-3">3.1 Permitted Content</h3>
                  <p className="text-green-900 mb-2">The following types of content are explicitly allowed and protected:</p>
                  <ul className="list-disc pl-6 space-y-1 text-green-900">
                    <li>Criticism of HOA Board decisions, policies, and governance practices</li>
                    <li>Questions about HOA operations, finances, and decision-making processes</li>
                    <li>Proposals for policy changes or alternative approaches</li>
                    <li>Sharing of official documents, financial records, and meeting minutes</li>
                    <li>Respectful disagreement and debate on community matters</li>
                    <li>Discussion of legal rights and options available to members</li>
                    <li>Requests for transparency and accountability</li>
                  </ul>
                </div>

                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <h3 className="text-lg font-semibold text-red-800 mb-3">3.2 Prohibited Content</h3>
                  <p className="text-red-900 mb-2">The following conduct is strictly prohibited:</p>
                  <ul className="list-disc pl-6 space-y-1 text-red-900">
                    <li>Harassment, threats, or intimidation of any member or board member</li>
                    <li>Defamatory statements or false accusations without factual basis</li>
                    <li>Discrimination based on race, religion, gender, age, or other protected characteristics</li>
                    <li>Spam, commercial solicitation, or off-topic content</li>
                    <li>Impersonation of other members or officials</li>
                    <li>Deliberate spread of misinformation or false information</li>
                    <li>Sharing of private information without consent</li>
                    <li>Content that violates applicable laws or regulations</li>
                  </ul>
                </div>

                <p className="text-gray-700 leading-relaxed">
                  <strong>3.3 Moderation Philosophy:</strong> This Platform moderates <em>conduct</em>, not <em>ideas</em>. Members may freely criticize HOA policies, question board decisions, and advocate for changes. Moderation focuses on maintaining respectful discourse, not suppressing dissenting viewpoints.
                </p>
              </div>
            </section>

            {/* User Privacy */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">4. User Privacy and Data Protection</h2>
              <div className="space-y-3 text-gray-700 leading-relaxed">
                <p><strong>4.1 Email Privacy:</strong> Member email addresses are never displayed publicly on the Platform. Email addresses are used solely for account verification, notifications (if enabled), and essential platform communications.</p>
                <p><strong>4.2 Anonymous Posting:</strong> Members may choose to post anonymously. When posting anonymously:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Your identity is hidden from other members</li>
                  <li>Moderators can see your actual identity for moderation purposes only</li>
                  <li>Anonymous posts are subject to the same content standards</li>
                  <li>Abuse of anonymous posting may result in loss of this privilege</li>
                </ul>
                <p><strong>4.3 Private Messaging:</strong> Direct messages between members are encrypted and private. The Platform does not access or monitor private messages except when required by law or in response to abuse reports.</p>
                <p><strong>4.4 Data Collection:</strong> We collect only information necessary for platform operation, including account details, posted content, and usage analytics. We do not sell or share member data with third parties.</p>
              </div>
            </section>

            {/* Data Retention */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Data Retention and Archives</h2>
              <div className="space-y-3 text-gray-700 leading-relaxed">
                <p><strong>5.1 Permanent Archive:</strong> All approved posts, comments, and documents become part of a permanent community archive. This ensures:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Historical record of community discussions and decisions</li>
                  <li>Accountability for statements and positions taken</li>
                  <li>Reference material for future governance decisions</li>
                  <li>Evidence preservation for potential legal matters</li>
                </ul>
                <p><strong>5.2 Content Deletion:</strong> Members cannot delete approved posts or comments. This policy maintains the integrity of discussions and prevents revisionist history. Members should carefully consider content before posting.</p>
                <p><strong>5.3 Account Deletion:</strong> Members may request account deletion, but previously posted content will remain in the archive with attribution removed or anonymized.</p>
              </div>
            </section>

            {/* Moderation */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Moderation and Enforcement</h2>
              <div className="space-y-3 text-gray-700 leading-relaxed">
                <p><strong>6.1 Pre-Moderation:</strong> New posts may be subject to pre-moderation review before publication. Moderators will approve or reject content based on compliance with content standards, not agreement with viewpoints.</p>
                <p><strong>6.2 Moderation Actions:</strong> Moderators may take the following actions:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Approve or reject posts and comments</li>
                  <li>Request edits or clarifications from authors</li>
                  <li>Lock threads to prevent further comments</li>
                  <li>Pin important announcements or discussions</li>
                  <li>Merge duplicate threads</li>
                  <li>Archive completed discussions</li>
                  <li>Suspend accounts for violations (7-30 days)</li>
                  <li>Ban accounts for serious or repeated violations</li>
                </ul>
                <p><strong>6.3 Transparency Reports:</strong> Moderators publish monthly transparency reports detailing all moderation actions, including approvals, rejections, suspensions, and bans, with explanations for each action.</p>
                <p><strong>6.4 Appeals Process:</strong> Members may appeal moderation decisions within 7 days by contacting the moderation team. Appeals are reviewed by a different moderator than the one who made the original decision.</p>
              </div>
            </section>

            {/* Liability */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Liability and Disclaimers</h2>
              <div className="space-y-3 text-gray-700 leading-relaxed">
                <p><strong>7.1 User-Generated Content:</strong> All content posted by members represents the views of those individual members, not the HOA, the Platform operators, or moderators. The Platform is not responsible for the accuracy, legality, or appropriateness of user-generated content.</p>
                <p><strong>7.2 Section 230 Protection:</strong> This Platform operates as an interactive computer service under 47 U.S.C. § 230 (Communications Decency Act). We are not liable for content posted by users.</p>
                <p><strong>7.3 DMCA Compliance:</strong> We comply with the Digital Millennium Copyright Act. Copyright infringement claims should be submitted to our designated agent with proper documentation.</p>
                <p><strong>7.4 No Legal Advice:</strong> Information shared on this Platform does not constitute legal advice. Members should consult qualified attorneys for legal guidance on HOA matters.</p>
                <p><strong>7.5 Platform Availability:</strong> While we strive for 99% uptime, we do not guarantee uninterrupted access. The Platform may be temporarily unavailable for maintenance or technical issues.</p>
              </div>
            </section>

            {/* Legal Hold */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Legal Hold and Record Keeping</h2>
              <div className="space-y-3 text-gray-700 leading-relaxed">
                <p><strong>8.1 Litigation Hold:</strong> In the event of actual or anticipated litigation involving the HOA, all relevant platform content will be preserved and protected from deletion or modification.</p>
                <p><strong>8.2 Legal Requests:</strong> We will comply with valid legal requests for information, including subpoenas and court orders, while protecting member privacy to the extent permitted by law.</p>
                <p><strong>8.3 Record Retention:</strong> All platform data, including deleted or rejected content, is retained in secure backups for legal and administrative purposes.</p>
              </div>
            </section>

            {/* Intellectual Property */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Intellectual Property</h2>
              <div className="space-y-3 text-gray-700 leading-relaxed">
                <p><strong>9.1 User Content:</strong> Members retain ownership of content they post. By posting, members grant the Platform a non-exclusive, perpetual license to display, store, and archive the content.</p>
                <p><strong>9.2 Official Documents:</strong> HOA official documents shared on the Platform remain the property of the HOA. Members may access and reference these documents for community purposes.</p>
                <p><strong>9.3 Platform Content:</strong> The Platform design, software, and original content are protected by copyright and may not be reproduced without permission.</p>
              </div>
            </section>

            {/* Changes to Terms */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Changes to Terms</h2>
              <div className="space-y-3 text-gray-700 leading-relaxed">
                <p><strong>10.1 Modifications:</strong> These Terms may be updated periodically. Material changes will be announced on the Platform with at least 30 days notice before taking effect.</p>
                <p><strong>10.2 Continued Use:</strong> Continued use of the Platform after changes take effect constitutes acceptance of the modified Terms.</p>
                <p><strong>10.3 Version History:</strong> All previous versions of these Terms are archived and available for reference.</p>
              </div>
            </section>

            {/* Governing Law */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Governing Law and Dispute Resolution</h2>
              <div className="space-y-3 text-gray-700 leading-relaxed">
                <p><strong>11.1 Jurisdiction:</strong> These Terms are governed by the laws of the State of Montana and applicable federal law.</p>
                <p><strong>11.2 Venue:</strong> Any legal disputes arising from these Terms or Platform use shall be resolved in the appropriate courts of Montana.</p>
                <p><strong>11.3 Mediation:</strong> Before pursuing legal action, parties agree to attempt good-faith mediation of disputes.</p>
              </div>
            </section>

            {/* Contact */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">12. Contact Information</h2>
              <div className="space-y-3 text-gray-700 leading-relaxed">
                <p>For questions about these Terms or the Platform, contact:</p>
                <div className="bg-gray-50 rounded-lg p-4 mt-4">
                  <p className="font-semibold text-gray-900">Lone Pine Public Square</p>
                  <p className="text-gray-700">Email: info@lonepinehoa.org</p>
                  <p className="text-gray-700">Location: Lone Pine, Montana</p>
                  <p className="text-gray-700">Timezone: Mountain Time (MST)</p>
                </div>
              </div>
            </section>

            {/* Acknowledgment */}
            <section className="bg-teal-50 border border-teal-200 rounded-lg p-6">
              <h2 className="text-xl font-bold text-teal-900 mb-3">Acknowledgment</h2>
              <p className="text-teal-900 leading-relaxed">
                By using Lone Pine Public Square, you acknowledge that you have read, understood, and agree to be bound by these Terms and Conditions. You also acknowledge that this Platform is designed to promote transparent community governance and that your participation contributes to a more accountable and engaged HOA community.
              </p>
            </section>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}